// src/mixins/i18n-monitor-mixin.js - 优化的语言监控混入
import { setI18nLanguage, supportedLanguages } from '@/lang/index'
import routerMixin from './router-mixin'

export default {
  mixins: [routerMixin],
  data() {
    return {
      languageChangeHandler: null
    }
  },
  
  computed: {
    currentLanguage() {
      return this.$store.getters['user/currentLanguage']
    },
    
    currentLanguageCode() {
      return this.$store.getters['user/currentLanguageCode']
    }
  },
  
  watch: {
    // 监听 Vuex 中的语言变化
    currentLanguageCode: {
      handler(newLang, oldLang) {
        if (newLang && newLang !== oldLang) {
          this.handleLanguageChange(newLang, oldLang)
        }
      },
      immediate: false
    },
    
    // 监听路由参数变化
    '$route.params.lang': {
      handler(newLang, oldLang) {
        if (newLang && newLang !== oldLang && supportedLanguages.includes(newLang)) {
          this.syncLanguageFromRoute(newLang)
        }
      },
      immediate: true
    }
  },
  
  created() {
    // 初始化语言设置
    this.initializeLanguage()
    
    // 监听全局语言切换事件
    this.languageChangeHandler = this.onLanguageChanged.bind(this)
    this.$eventBus?.$on('language-changed', this.languageChangeHandler)
  },
  
  beforeDestroy() {
    // 清理事件监听
    if (this.languageChangeHandler) {
      this.$eventBus?.$off('language-changed', this.languageChangeHandler)
    }
  },
  
  methods: {
    // 初始化语言设置
    async initializeLanguage() {
      try {
        await this.$store.dispatch('user/initLanguage')
        const routeLang = this.$route.params.lang
        const storeLang = this.currentLanguageCode
        
        // 同步路由和状态
        if (routeLang && routeLang !== storeLang) {
          await this.$store.dispatch('user/toggleLanguage', routeLang)
        }
        
        // 同步到 i18n
        setI18nLanguage(this.currentLanguageCode)
      } catch (error) {
        console.error('Language initialization failed:', error)
      }
    },
    
    // 处理语言变化
    handleLanguageChange(newLang, oldLang) {
      if (!supportedLanguages.includes(newLang)) {
        console.warn(`Unsupported language: ${newLang}`)
        return
      }
      
      // 同步到 vue-i18n
      setI18nLanguage(newLang)
      
      // 更新 HTML lang 属性
      document.documentElement.setAttribute('lang', newLang)
      
      // 可选：触发组件级回调
      if (typeof this.onLanguageChange === 'function') {
        this.onLanguageChange(newLang, oldLang)
      }
      
    },
    
    // 从路由同步语言
    async syncLanguageFromRoute(routeLang) {
      if (routeLang !== this.currentLanguageCode) {
        try {
          await this.$store.dispatch('user/toggleLanguage', routeLang)
        } catch (error) {
          console.error('Failed to sync language from route:', error)
        }
      }
    },
    
    // 全局语言切换事件处理
    onLanguageChanged(languageObj) {
      if (languageObj && languageObj.country) {
        setI18nLanguage(languageObj.country)
      }
    },
    
    // 切换语言的公共方法（优化版）
    async switchLanguage(langCode) {
      if (!supportedLanguages.includes(langCode)) {
        console.error(`Language ${langCode} is not supported`)
        return { success: false, error: 'Unsupported language' }
      }
      
      try {
        // 1. 更新 Vuex 状态
        const languageObj = await this.$store.dispatch('user/toggleLanguage', langCode)
        
        // 2. 更新 vue-i18n
        setI18nLanguage(langCode)
        
        // 3. 等待状态更新完成
        await this.$nextTick()
        
        // 4. 使用路由助手切换语言（自动处理路由更新）
        const result = await this.$switchLanguage(langCode)
        
        if (result.success) {
          // 5. 再次等待状态同步
          await this.$nextTick()
          
          // 6. 强制更新组件
          this.$forceUpdate()
          
          // 7. 触发全局事件（用于组件刷新）
          this.$nextTick(() => {
            // 触发全局事件
            if (this.$eventBus) {
              this.$eventBus.$emit('language-switched', {
                from: result.from,
                to: result.to,
                language: languageObj,
                timestamp: Date.now()
              })
            }
          })
          
          return { success: true, ...result, language: languageObj }
        } else {
          // console.error('Language switch failed:', result.error)
          return { success: false, error: result.error }
        }
      } catch (error) {
        // console.error('Language switch failed:', error)
        return { success: false, error: error.message }
      }
    },
    
    // 获取可用语言列表
    getAvailableLanguages() {
      return this.$store.getters['user/availableLanguages']
    }
  }
}